import React, { useEffect, useState } from 'react';
import './ImageUpload.css';

import { ReactComponent as UploadIcon } from '../../../../../ui/src/assets/studies-icons/uploadHeroIcon.svg';
import { useFileUpload } from '../../../providers/files-upload/filesUpload';
import DragAndDropFiles from '../drag-drop-files/DragDropFiles';

import { Dialog, Button } from '../../custom-ui';
import ProgressLoad from '../../../../../ui/src/components/new-components';
import { toast } from 'react-toastify';
import ApiServices from '../../ApiServices/ApiServices';
import { buildPatientDisplayName } from '../../utils/nameUtils';

interface Patient {
    patientID: string;
    name: string;
}

const ImageUpload: React.FC = () => {
    const {
        selectedImages,
        uploading,
        resetFiles,
        handleFileChange,
        uploadProgress,
        handleModalFileUpload,
        showModal,
        setShowModal,
    } = useFileUpload();

    const [patients, setPatients] = useState<Patient[]>([]);
    const [selectedPatient, setSelectedPatient] = useState<string>('');

    /* Fetch patients when modal opens (kept behavior) */
    useEffect(() => {
        if (!showModal) return;

        const fetchPatients = async () => {
            try {
                const res = await ApiServices.getAllPatients();
                const formatted: Patient[] = (res?.items || []).map((p: any) => ({
                    patientID: p.PK,
                    name: buildPatientDisplayName(p),
                }));
                setPatients(formatted);
                if (formatted.length > 0) setSelectedPatient(formatted[0].patientID);
            } catch (err) {
                console.error('Error fetching patients:', err);
            }
        };

        fetchPatients();
    }, [showModal]);

    const cleanUpAll = () => {
        resetFiles();
        setShowModal(false);
    };

    const finalUpload = async () => {
        if (!selectedPatient) {
            toast.error('Please select a patient.');
            return;
        }
        await handleModalFileUpload(selectedPatient);
        cleanUpAll();
    };

    return (
        <>
            {/* Trigger button (unchanged behavior) */}
            <Button
                variant="primary"
                size="small"
                className="flex items-center"
                onClick={() => setShowModal(true)}
                leftIcon={<UploadIcon className="h-4 w-4" />}
            >
                <span className="hidden md:inline">Upload Files</span>
            </Button>

            {/* Shared Dialog — consistent with app */}
            <Dialog
                isOpen={showModal}
                onClose={cleanUpAll}
                size="large"
                title="Upload files"
                description="Attach DICOM or ZIP files to a patient."
                footer={
                    <div className="flex w-full items-center justify-between gap-3">
                        {/* Selection count (kept) */}
                        <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                            {selectedImages?.length
                                ? `Total selected files: ${selectedImages.length}`
                                : 'No files selected yet'}
                        </div>

                        <div className="flex items-center gap-2">
                            <Button
                                variant="danger"
                                onClick={cleanUpAll}
                                disabled={uploading}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={finalUpload}
                                disabled={uploading || !selectedImages?.length}
                                loading={uploading}
                            >
                                {uploading ? 'Uploading…' : 'Upload'}
                            </Button>
                        </div>
                    </div>
                }
            >
                {/* Modal body */}
                <div className="space-y-6">
                    {/* Header hint row */}
                    <div className="flex flex-wrap items-center justify-between gap-2">
                        <div className="inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-[14px] shadow-sm" style={{ borderColor: 'var(--border-subtle)', background: 'var(--bg-surface)', color: 'var(--text-secondary)' }}>
                            <span className="inline-flex h-2 w-2 rounded-full bg-primary-500" />
                            Allowed types: <code className="font-semibold">.dcm</code>,{' '}
                            <code className="font-semibold">.zip</code>
                        </div>
                        {selectedImages?.length ? (
                            <div className="text-[13px]">
                                Tip: You can drag more files into the area below.
                            </div>
                        ) : (
                            <div className="text-[13px]">
                                Tip: Drag & drop files or click to browse.
                            </div>
                        )}
                    </div>

                    {/* Patient select */}
                    <div className="space-y-1.5">
                        <label
                            htmlFor="patient-select"
                            className="block text-sm font-medium" style={{ color: 'var(--text-primary)' }}
                        >
                            Select patient
                        </label>
                        <select
                            id="patient-select"
                            value={selectedPatient}
                            onChange={e => setSelectedPatient(e.target.value)}
                            disabled={uploading}
                            className="
                w-full rounded-lg border p-2.5 text-sm shadow-sm outline-none
                focus:border-primary-400 focus:ring-2 focus:ring-primary-200
              "
                            style={{ borderColor: 'var(--border-subtle)', background: 'var(--bg-surface)', color: 'var(--text-primary)' }}
                        >
                            {patients.map(patient => (
                                <option key={patient.patientID} value={patient.patientID}>
                                    {patient.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Drag & drop area / upload progress */}
                    <div
                        className="
              rounded-xl border border-dashed p-5
            "
                        style={{ borderColor: 'var(--border-subtle)', background: 'var(--bg-surface)' }}
                    >
                        {uploading ? (
                            <div className="flex min-h-[220px] items-center justify-center">
                                {/* Keep your existing progress component */}
                                <ProgressLoad progress={uploadProgress} />
                            </div>
                        ) : (
                            <div className="min-h-[220px]">
                                <DragAndDropFiles
                                    handleChange={handleFileChange}
                                    name="file"
                                    multiple
                                    accept=".dcm,.zip"
                                />
                            </div>
                        )}
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default ImageUpload;
